import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/react-hooks'

import getDeviceInfo from '../../../lib/device-info'
import getContentFromQuery from '../utils/get-content-from-query'
import getContentType from '../utils/get-content-type'
import contentItemQuery from '../utils/query/content-item-query.gql'
import accountQuery from '../utils/query/account-query.gql'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'

const AnalyticsDataContext = React.createContext()
const { Provider, Consumer: AnalyticsDataConsumer } = AnalyticsDataContext

function AnalyticsDataProviderComponent(props) {
  const isAuthenticated = useIsAuthenticated()
  const {
    children, id, isTrailer, duration
  } = props
  const deviceInfo = useMemo(() => getDeviceInfo(), [])
  // contentItemQuery
  const {
    data: contentItemData,
    loading: contentItemLoading,
    error: contentItemError
  } = useQuery(contentItemQuery, {
    variables: { contentId: id },
    fetchPolicy: 'no-cache' // apollo throws caching error when policy uses cache
  })
  // accountQuery
  const {
    data: accountData,
    loading: accountLoading,
    error: accountError
  } = useQuery(accountQuery, { skip: !isAuthenticated })

  if (contentItemLoading || contentItemError || !contentItemData) return null
  if (isAuthenticated && (accountLoading || accountError || !accountData)) return null
  const contentItemPayload = getContentFromQuery(contentItemData)
  const contentType = getContentType(contentItemPayload, isTrailer)
  const account = !isAuthenticated ? null : accountData.account
  const contentItem = contentItemData.contentItem
  const title = contentItemPayload.title
  const houseId = contentItemPayload.ldId || null

  return (
    <Provider
      value={{
        deviceInfo,
        account,
        contentItem,
        title,
        houseId,
        contentType,
        duration
      }}
    >
      {children}
    </Provider>
  )
}

AnalyticsDataProviderComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isTrailer: PropTypes.bool.isRequired,
  duration: PropTypes.number.isRequired
}

/**
 * Expose a memoized Provider({@link AnalyticsDataProviderComponent}) that only rerenders when id of the playing content(trailer) changes.
 * It calls contentItemQuery, accountQuery, and provides results to children.
 */
const AnalyticsDataProvider = React.memo(
  AnalyticsDataProviderComponent,
  (prevProps, nextProps) => prevProps.id === nextProps.id
)

export { AnalyticsDataProvider, AnalyticsDataConsumer }
